import * as THREE from "three";
import AppData from "../app.data";
import { Loader } from "../loaders/assets/loader";
import PhysicsService from "../services/physics/physics.service";
import { getAnimationRoot, isObjectInActiveScene } from "../utils/runtime-registry.utils";

const appData = new AppData();

export class SceneUtils {
    loader: Loader;
    private frustum = new THREE.Frustum();
    private projectionMatrix = new THREE.Matrix4();
    private cameraPosition = new THREE.Vector3();
    private ray = new THREE.Ray();
    private center = new THREE.Vector3();
    private modelPosition = new THREE.Vector3();
    private boundingBox = new THREE.Box3();

    constructor() {
        this.loader = new Loader();
    }

    public rotateArtworksBasedOnCameraPosition(target: THREE.Vector3) {
        if (appData.lookAtCameraModels.length > 0) {
            // Make models rotate on Y-axis to face the camera
            appData.lookAtCameraModels.forEach((model: THREE.Object3D) => {
                if (!isObjectInActiveScene(model)) return;
                // Get model position
                model.getWorldPosition(this.modelPosition);

                // Calculate direction from model to camera (only on XZ plane for Y-axis rotation)
                const dx = target.x - this.modelPosition.x;
                const dz = target.z - this.modelPosition.z;

                // Calculate angle and set Y rotation
                model.rotation.y = Math.atan2(dx, dz);
            });
        }
    }

    public interpolateModelsEmissionBasedOnCameraPosition(camera: THREE.Camera, modelsArray: any[], maxDistance: number) {
        const cameraPosition = camera.position;
        for (let index = 0; index < modelsArray.length; index++) {
            const model = modelsArray[index];
            if (!isObjectInActiveScene(model)) continue;
            model.getWorldPosition(this.modelPosition);
            const distance = cameraPosition.distanceTo(this.modelPosition);
            if (distance <= maxDistance && model.material) {
                model.material.emissiveIntensity = this.interpolate(0, distance, maxDistance, 30, 0.01);
            }
        }
    }

    public interpolate(x1: number, x: number, x2: number, y1: number, y2: number) {
        const t = (x - x1) / (x2 - x1);
        const easedT = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        return y1 + easedT * (y2 - y1);
    }

    public updateMaterialOverrides(data: any) {
        if (!data.meshName) return;
        if (data.textureUrl && data.textureUrl.length > 5) {
            this.loader.loadTexture(data.textureUrl).then((texture: any) => {
                if (texture) {
                    texture.repeat.set(data.repeat, data.repeat);
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    const mesh = appData.spaceScene.getObjectByName(data.meshName);
                    if (mesh instanceof THREE.Mesh && mesh.material) {
                        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                        materials.forEach((material: THREE.Material) => {
                            (material as any)[data.channel] = texture;
                            material.needsUpdate = true;
                        });
                    }
                }
            });
            return;
        }
        const mesh = appData.spaceScene.getObjectByName(data.meshName);
        if (mesh instanceof THREE.Mesh && mesh.material) {
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            materials.forEach((material: THREE.Material) => {
                (material as any)[data.channel] = null;
                material.needsUpdate = true;
            });
        }
    }

    public skinnedMeshesAreInFrustum(physicsService: PhysicsService | undefined) {
        const skinnedMeshes = appData.skinnedMeshes;
        if (skinnedMeshes.length === 0 || !appData.camera) return false;

        this.projectionMatrix.multiplyMatrices(appData.camera.projectionMatrix, appData.camera.matrixWorldInverse);
        this.frustum.setFromProjectionMatrix(this.projectionMatrix);
        appData.camera.getWorldPosition(this.cameraPosition);
        this.ray.origin.copy(this.cameraPosition);

        for (let index = 0; index < skinnedMeshes.length; index++) {
            const root = getAnimationRoot(skinnedMeshes[index]);
            if (!root || !isObjectInActiveScene(root)) continue;

            let boundingObject = root.getObjectByName("bounding-cylinder");
            if (!boundingObject) {
                boundingObject = root;
            }

            this.boundingBox.setFromObject(boundingObject);
            if (this.frustum.intersectsBox(this.boundingBox)) {

                // Efficient occlusion check using Physics Octree
                if (physicsService && physicsService.worldOctree && physicsService.colideSet) {
                    this.boundingBox.getCenter(this.center);

                    // Calculate distance to object center
                    const distanceToObject = this.cameraPosition.distanceTo(this.center);

                    // Set ray direction to object center
                    this.ray.direction.copy(this.center).sub(this.cameraPosition).normalize();

                    // Check for intersection with world geometry (walls, etc.)
                    const hit = physicsService.worldOctree.rayIntersect(this.ray);

                    // If we hit something closer than the object (with small buffer), it's occluded
                    // Using 0.2 buffer to prevent self-occlusion artifacts if object is near wall
                    if (hit && hit.distance < distanceToObject - 0.2) {
                        continue;
                    }
                }

                return true;
            }
        }
        return false;
    }

    public videoIsInFrustum() {
        const videos = appData.videoList;
        if (!videos.length || !appData.camera) return false;
        this.projectionMatrix.multiplyMatrices(appData.camera.projectionMatrix, appData.camera.matrixWorldInverse);
        this.frustum.setFromProjectionMatrix(this.projectionMatrix);

        for (let index = 0; index < videos.length; index++) {
            const video = videos[index];
            const artwork = video.artworkModel;
            const element = video.video as HTMLVideoElement | undefined;
            const isPlaying = element && !element.paused && !element.ended && element.readyState > 1;
            if (isPlaying && artwork && isObjectInActiveScene(artwork)) {
                this.boundingBox.setFromObject(artwork);
                if (this.frustum.intersectsBox(this.boundingBox)) return true;
            }
        }
        return false;
    }
}

